import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { Container, Graphics } from "pixi.js";
import { Group } from "tweedle.js";
import { TransitionComponent } from "../components/Transition/TransitionComponent";
import { ScarabWinsComponent } from "../components/jackpot/ScarabWinsComponent";
import { LogoComponent } from "../components/logo/LogoComponent";
import { MeterComponent } from "../components/meter/MeterComponent";
import { ReelFrameComponent } from "../components/reels/ReelFrameComponent";
import { ReelComponent } from "../components/reels/ReelsComponent";
import { TrailsComponent } from "../components/trail/TrailsComponent";
import { getMobileDetect } from "../config/GlobalVariableConfig";
import { Manager } from "../core/Manager";
import { DisplayComponent } from "../core/displays/DisplayComponent";
import { DisplayComponentFactory } from "../core/displays/DisplayComponentFactory";
import { DisplayObject } from "../core/displays/DisplayObject";
import { IScene } from "../core/interfaces";
import { Reels } from "../core/reels/Reels";
import { GameConstants } from "../gameClientEvents/GameConstants";
import { BigWin } from "./BigWin";

export abstract class BaseScene extends Container implements IScene {
    assetBundles: string[] = [];
    assets: string[] = [];
    protected game = new Container();
    protected reelFrame = new Container();
    protected backgroundImageCont = new Container();
    protected backgroundSpine: Spine | null = null;
    protected reels: Reels | null = null;
    protected bigWin: BigWin | null = null;
    protected tweenGroup = new Group();
    protected graphics: Graphics;
    protected graphicsOffset: number = 830;

    constructor() {
        super();
    }

    public abstract constructorWithAssets(): Promise<void>;

    protected initBaseLayering(bgKey: string, spineSkel: string, spineAtlas: string) {
        this.initBackground(bgKey, spineSkel, spineAtlas);
        this.game.addChild(this.reelFrame);
        this.addChild(this.game);
    }

    protected addComponent(Comp: any, parent?: Container): any {
        DisplayComponent.register(new DisplayComponentFactory(Comp));
        const component = DisplayComponent.get(Comp);
        component.init();
        //@ts-ignore
        const instance = component.getInstance();
        parent?.addChild(instance);
        return instance;
    }

    protected setupCommonUI() {
        this.addComponent(LogoComponent, this.game);
        this.addComponent(MeterComponent, this.game);
        this.addComponent(TransitionComponent, this);
        this.addComponent(ScarabWinsComponent);
        this.addComponent(TrailsComponent, this.game);
    }

    //@ts-ignore
    protected initBackground(bgKey: string, spineSkel: string, spineAtlas: string) {
        //this.backgroundImageCont.addChild(Sprite.from(bgKey));
        this.addChild(this.backgroundImageCont);

        this.backgroundSpine = Spine.from({ skeleton: spineSkel, atlas: spineAtlas });
        this.backgroundSpine.position.set(GameConstants.BACKGROUND_SIZE / 2, GameConstants.BACKGROUND_SIZE / 2);
        this.addChild(this.backgroundSpine);
    }

    protected setupReelFrame(skel: string, atlas: string, animate: boolean) {
        DisplayComponent.register(new DisplayComponentFactory(ReelFrameComponent));
        DisplayComponent.get(ReelFrameComponent).setAnim(skel, atlas);
        const reelBack = DisplayComponent.get(ReelFrameComponent).getInstance();
        this.reels?.addChildAt(reelBack, 0);
        this.reelFrame.addChild(this.reels);

        this.reelFrame.eventMode = "static";

        this.graphics = new Graphics()
            .rect(0, 0, 1300, 1250)
            .fill(0xDE3249);

        this.graphics.alpha = 0.01;
        this.reels?.addChild(this.graphics);
        reelBack.position.set(this.graphics.width / 2 - 10, this.graphics.height / 2 - 100);
        this.reels?.pivot.set(this.graphics.width / 2, this.graphicsOffset);
        this.reels?.position.set(GameConstants.BACKGROUND_SIZE / 2, GameConstants.BACKGROUND_SIZE / 2 - this.graphicsOffset);

        const alpha = animate ? 0 : 1;
        this.reelFrame.alpha = this.backgroundImageCont.alpha = this.backgroundSpine!.alpha = alpha;
        this.backgroundSpine!.state.setAnimation(0, "animation", true);
    }

    public update(): void {
        this.tweenGroup?.update();
        this.reels?.update();
        this.bigWin?.update();
    }

    public resize(screenWidth: number, screenHeight: number): void {
        const isLandscape = screenWidth > screenHeight;
        const scale = isLandscape ? Math.min(screenWidth / GameConstants.GAME_RES_WIDTH, screenHeight / GameConstants.GAME_RES_HEIGHT) : Math.min(screenWidth / GameConstants.GAME_RES_HEIGHT, screenHeight / GameConstants.GAME_RES_WIDTH);
        const bgScale = Math.max(scale, screenWidth / GameConstants.BACKGROUND_SIZE, screenHeight / GameConstants.BACKGROUND_SIZE);

        this.game.scale.set(scale);
        this.game.position.set(-(GameConstants.BACKGROUND_SIZE * scale - screenWidth) / 2, -(GameConstants.BACKGROUND_SIZE * scale - screenHeight) / 2);

        this.backgroundImageCont?.scale.set(bgScale);
        this.backgroundImageCont?.position.set(-(GameConstants.BACKGROUND_SIZE * bgScale - screenWidth) / 2, -(GameConstants.BACKGROUND_SIZE * bgScale - screenHeight) / 2);

        this.backgroundSpine?.scale.set(bgScale);
        this.backgroundSpine?.position.set(screenWidth / 2, screenHeight / 2);

        this.reelFrame.scale.set(isLandscape ? ( getMobileDetect() ? 0.75 : 0.9) : 1);
        this.reelFrame.position.set(isLandscape ? ( getMobileDetect() ? 390 : 420) : 10, isLandscape ? ( getMobileDetect() ? 310 : 190) : 290);

        this.reels?.position.set(GameConstants.BACKGROUND_SIZE / 2, (GameConstants.BACKGROUND_SIZE - 830));

        [this.game, this].forEach(cont => cont.children.forEach(c => {
            if (c instanceof DisplayObject) c.setPositionResize(Manager.Orientation);
        }));

        this.bigWin?.scale.set(scale);
        this.bigWin?.position.set(screenWidth / 2, screenHeight / 2);
    }

    override destroy() {
        [LogoComponent, ReelComponent, MeterComponent, TransitionComponent, ReelFrameComponent, ScarabWinsComponent, TrailsComponent]
            .forEach((c: any) => DisplayComponent.unregister(c));
        this.reels?.destroy();
        this.reels = null;
        this.tweenGroup?.removeAll();
        this.tweenGroup = null;
        super.destroy({ children: true });
    }
}
